import { readFileText, setupMarkdownToolPage } from './shared/index.js';
import {
  JSON_MAX_BYTES,
  validateJsonInput,
} from './formats/json/convert.js';
import JsonWorker from './formats/json/worker.js?worker';

const WORKER_TIMEOUT_MS = 30_000;

function sourceEditor(root) {
  return root.querySelector('#source-text, #json-source, textarea[name="source"]');
}

function outputMode(root) {
  return root.querySelector('#json-output-mode, #output-mode, select[name="mode"]');
}

function runWorker(WorkerConstructor, payload, signal) {
  if (typeof Worker === 'undefined') return Promise.reject(new Error('This browser cannot start the local conversion worker.'));
  if (signal?.aborted) return Promise.reject(new DOMException('Conversion canceled.', 'AbortError'));
  return new Promise((resolve, reject) => {
    const worker = new WorkerConstructor({ name: 'kivoza-json-to-markdown' });
    let settled = false;
    let timeoutId;
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', cancel);
      worker.terminate();
      callback(value);
    };
    const cancel = () => finish(reject, new DOMException('Conversion canceled.', 'AbortError'));
    timeoutId = setTimeout(() => finish(reject, new Error('JSON conversion timed out after 30 seconds.')), WORKER_TIMEOUT_MS);
    signal?.addEventListener('abort', cancel, { once: true });
    worker.onmessage = (event) => {
      if (event.data?.ok) finish(resolve, event.data.result);
      else finish(reject, new Error(event.data?.error || 'JSON conversion failed.'));
    };
    worker.onerror = (event) => finish(reject, new Error(event.message || 'JSON conversion worker failed.'));
    worker.postMessage(payload);
  });
}

export function initJsonPage(root = document) {
  const source = sourceEditor(root);
  const modeControl = outputMode(root);
  const indentControl = root.querySelector('#json-indent, select[name="indent"]');
  const preserveBigInt = root.querySelector('#json-bigint, input[name="preserveBigInt"]');
  let sourceFile = null;
  let reading = false;
  const page = setupMarkdownToolPage({
    root,
    defaultFileName: 'json-document',
    onFiles: async (files) => {
      const file = files?.[0];
      if (!file) return;
      if (files.length > 1) { page.setStatus('Choose one JSON file at a time.', 'error'); return; }
      if (!/\.json$/i.test(file.name) && file.type && !/json/i.test(file.type)) {
        page.setStatus('Choose a JSON file.', 'error');
        return;
      }
      if (reading) return;
      page.setReady(false);
      reading = true;
      try {
        const text = await readFileText(file, JSON_MAX_BYTES, 'JSON file');
        sourceFile = file;
        if (source) source.value = text;
        page.setReady(true);
        page.setStatus(`${file.name} is ready. Choose an output view.`, 'success');
      } catch (error) {
        sourceFile = null;
        page.setStatus(error instanceof Error ? error.message : 'The JSON file could not be read.', 'error');
      } finally {
        reading = false;
      }
    },
  });
  const convertButton = page.elements.convertButton;
  convertButton?.addEventListener('click', () => {
    const text = source?.value || '';
    const mode = modeControl?.value || 'auto';
    const indent = indentControl?.value || 2;
    const problem = validateJsonInput(text, { mode, indent });
    if (problem) { page.setStatus(problem, 'error'); return; }
    page.result.clear();
    page.runConversion(async ({ signal, report }) => {
      report('Parsing JSON locally…', 0.2);
      const result = await runWorker(JsonWorker, {
        source: text,
        options: { mode, indent, preserveBigInt: preserveBigInt ? Boolean(preserveBigInt.checked) : true },
      }, signal);
      report('Markdown is ready.', 1);
      return result;
    }).catch(() => {});
  });
  page.elements.clearButton?.addEventListener('click', () => {
    sourceFile = null;
    if (source) source.value = '';
    page.setReady(false);
    page.result.clear();
    page.setStatus('Source cleared.');
  });
  source?.addEventListener('input', () => {
    sourceFile = null;
    const mode = modeControl?.value || 'auto';
    const indent = indentControl?.value || 2;
    page.setReady(!validateJsonInput(source.value, { mode, indent }));
  });
  page.setReady(Boolean(source?.value?.trim()) && !validateJsonInput(source.value, { mode: modeControl?.value || 'auto', indent: indentControl?.value || 2 }));
  return { page, get sourceFile() { return sourceFile; } };
}

if (typeof document !== 'undefined' && document.documentElement.dataset.markdownFormat === 'json') initJsonPage();
